/**
 * Hook for managing per-message and conversation-level internal comments.
 * Comments are stored in contacts.internal_comments (jsonb array).
 */

import { useState, useCallback } from "react"
import { supabase } from "@/lib/data"
import { MessageComment } from "../types"

export type { MessageComment }

export function useComments(contactId: string) {
  const [saving, setSaving] = useState(false)

  const getComments = useCallback(async (): Promise<MessageComment[]> => {
    try {
      const { data, error } = await supabase
        .from("contacts")
        .select("internal_comments")
        .eq("id", contactId)
        .single()

      if (error || !data) return []
      const raw = data.internal_comments
      if (!raw) return []
      const arr = Array.isArray(raw) ? raw : []
      return arr.map((entry: any, index: number): MessageComment => {
        const text       = entry?.text ?? ""
        const authorName = entry?.author_name ?? ""
        const authorId   = String(entry?.author_id ?? "")
        let created_at   = entry?.created_at
        if (typeof created_at === "string") created_at = new Date(created_at).getTime() / 1000
        created_at = Number(created_at)
        if (!Number.isFinite(created_at) || created_at <= 0) created_at = Math.floor(Date.now() / 1000)
        else if (created_at > 1e12) created_at = Math.floor(created_at / 1000)
        const messageId = entry?.message_id ? String(entry.message_id) : undefined
        const id = entry?.id ?? `cmt_${created_at}_${index}`
        return { id, text, author_id: authorId, author_name: authorName, created_at, ...(messageId ? { message_id: messageId } : {}) }
      })
    } catch {
      return []
    }
  }, [contactId])

  const addComment = useCallback(async (
    text: string,
    authorId: string,
    authorName: string,
    messageId?: string
  ): Promise<MessageComment[]> => {
    setSaving(true)
    try {
      const existing = await getComments()
      const newComment: MessageComment = {
        id: `cmt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        text,
        author_id: authorId,
        author_name: authorName,
        created_at: Math.floor(Date.now() / 1000),
        ...(messageId ? { message_id: messageId } : {}),
      }
      const updated = [...existing, newComment]
      const { error } = await supabase
        .from("contacts")
        .update({ internal_comments: updated as any })
        .eq("id", contactId)
      if (error) throw error
      return updated
    } finally {
      setSaving(false)
    }
  }, [contactId, getComments])

  const deleteComment = useCallback(async (commentId: string): Promise<MessageComment[]> => {
    setSaving(true)
    try {
      const existing = await getComments()
      const updated = existing.filter(c => c.id !== commentId)
      const { error } = await supabase
        .from("contacts")
        .update({ internal_comments: updated as any })
        .eq("id", contactId)
      if (error) throw error
      return updated
    } finally {
      setSaving(false)
    }
  }, [contactId, getComments])

  const getCommentsForMessage = useCallback((comments: MessageComment[], messageId: string) => {
    return comments.filter(c => c.message_id === messageId)
  }, [])

  const getConversationComments = useCallback((comments: MessageComment[]) => {
    return comments.filter(c => !c.message_id)
  }, [])

  return { getComments, addComment, deleteComment, getCommentsForMessage, getConversationComments, saving }
}
